import { isFunction as lo_isFunction } from 'es-toolkit';
import { noop } from '#x1-base/utils';

const now = () => performance.now();

type TimerCallback = (dt: number) => void;

export class Timer extends EventTarget {
    public count: number = 0;

    private running: boolean = false;
    private last: number = 0;
    private _interval: number = 1000;
    private frame: number = 0;
    private fn: TimerCallback = noop;
    private fireFn: TimerCallback = noop;

    constructor(fn?: TimerCallback, interval = 1000) {
        super();
        this.last = now();
        this.fn = fn || noop;
        this._interval = interval;
        this.fireFn = (dt: number) => {
            if (lo_isFunction(this.fn)) {
                this.fn.call(this, dt);
            }
        };
    }

    public get interval(): number {
        return this._interval;
    }

    public set interval(value: number) {
        this._interval = value;
    }

    public start(fn?: TimerCallback): void {
        if (this.running) {
            return;
        }

        if (lo_isFunction(fn)) {
            this.fn = fn;
        }

        this.running = true;
        this.last = now();
        this.frame = requestAnimationFrame(() => this.tick());
        this.dispatchEvent(new Event('start'));
    }

    public stop(): void {
        if (!this.running) {
            return;
        }

        this.running = false;

        if (this.frame !== 0) {
            cancelAnimationFrame(this.frame);
            this.frame = 0;
        }

        this.dispatchEvent(new Event('stop'));
    }

    public reset(): void {
        const wasRunning = this.running;
        this.stop();
        this.count = 0;
        this.last = now();
        this.dispatchEvent(new Event('reset'));

        if (wasRunning) {
            this.start();
        }
    }

    public destroy(): void {
        this.stop();
        this.fn = noop;
        this.fireFn = noop;
    }

    private tick(): void {
        if (!this.running) {
            return;
        }

        this.frame = requestAnimationFrame(() => this.tick());
        const time = now();
        const dt = time - this.last;

        if (dt >= this._interval) {
            this.last = time;
            this.count++;
            this.fireFn(dt);
            this.dispatchEvent(new Event('tick'));
        }
    }
}
